<app-side-dialog [disableClose]="true" (closeDialog)="onCloseClick()">
  <div dialogHeader>
    <h2>
      @if (isCreateMode) {
        {{ 'pages.settings.userManagement.addCustomRoleDialog.titleCreate' | translate }}
      } @else if (isEditMode) {
        {{ 'pages.settings.userManagement.addCustomRoleDialog.titleEdit' | translate }}
      } @else {
        {{ 'pages.settings.userManagement.addCustomRoleDialog.titleView' | translate }}
      }
    </h2>
  </div>

  <div dialogContent class="flex flex-col">
    <form [formGroup]="form" class="flex flex-col gap-6 custom-form" (ngSubmit)="onSaveClick()">
      <div class="flex items-center gap-3">
        <app-icon-badge
          color="var(--shikenso-purple-base)"
          backgroundColor="var(--shikenso-pink-100-40)"
          [content]="identityIcon"
          [size]="48"
          [iconSize]="24"
          [borderRadius]="100"
          alt=""
        />

        @if (isViewMode) {
          <div class="flex flex-col min-w-0 gap-1">
            <div class="flex items-center gap-2">
              <span class="font-bold text-base">{{ getIdentityName() }}</span>

              @if (isProtected) {
                <div class="flex items-center gap-1 rounded-full bg-shikenso-gray-base px-2 py-1">
                  <app-icon src="/icons/lock.svg" [size]="12" alt="" />
                  <span class="text-xs text-shikenso-purple-base-60">
                    {{ 'pages.settings.userManagement.addCustomRoleDialog.systemRoleBadge' | translate }}
                  </span>
                </div>
              } @else if (exceedsCurrentUserPermissions()) {
                <app-icon-badge
                  content="/icons/lock.svg"
                  alt=""
                  color="var(--shikenso-warning-base)"
                  [size]="20"
                  [iconSize]="16"
                  [borderRadius]="100"
                />
              }
            </div>

            @if (isProtected) {
              <span class="text-xs text-shikenso-purple-base-60">
                {{ 'pages.settings.userManagement.addCustomRoleDialog.systemRoleCaption' | translate }}
              </span>
            } @else if (exceedsCurrentUserPermissions()) {
              <span class="text-xs text-shikenso-purple-base-60">
                {{ 'pages.settings.userManagement.addCustomRoleDialog.cannotEditRolePermissionMessage' | translate }}
              </span>
            }
          </div>
        } @else {
          <mat-form-field class="w-full">
            <input
              matInput
              formControlName="name"
              [placeholder]="'pages.settings.userManagement.addCustomRoleDialog.namePlaceholder' | translate"
            />
            <mat-error [control]="form.get('name')" />
          </mat-form-field>
        }
      </div>

      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <div class="flex flex-col">
            <span class="text-base font-bold">
              @if (isCreateMode) {
                {{ 'pages.settings.userManagement.addCustomRoleDialog.headingCreate' | translate }}
              } @else {
                {{ 'pages.settings.userManagement.addCustomRoleDialog.headingEditOrView' | translate }}
              }
            </span>
            <span class="text-xs text-shikenso-purple-base-60">
              {{ 'pages.settings.userManagement.addCustomRoleDialog.headingDescription' | translate }}
            </span>
          </div>

          <button
            mat-flat-button
            type="button"
            color="secondary-gray"
            class="mat-tiny-button"
            (click)="onExpandCollapseAllClick()"
          >
            <span class="text-xs">
              {{ allSectionsExpanded() ? ('shared.collapseAll' | translate) : ('shared.expandAll' | translate) }}
            </span>
          </button>
        </div>

        <div class="flex flex-col gap-3">
          @for (section of sections; track section.key) {
            <mat-expansion-panel
              class="cta-summary-expansion-panel cta-summary-expansion-panel-creator"
              [expanded]="isSectionExpanded(section.key)"
              (expandedChange)="onSectionToggle(section.key, $event)"
            >
              <mat-expansion-panel-header class="!px-4 !py-3">
                <mat-panel-title>
                  <div class="flex flex-1 items-center justify-between">
                    <div class="flex flex-col justify-center">
                      <span class="text-base font-bold">{{ section.titleKey | translate }}</span>
                      <span class="text-xs text-shikenso-purple-base-60">{{ section.descriptionKey | translate }}</span>
                    </div>

                    @let sectionEnabledCount = getSectionEnabledCount(section);

                    @if (sectionEnabledCount > 0 && sectionEnabledCount < section.toggles.length) {
                      <span class="text-sm text-shikenso-purple-base-60 mr-2">
                        {{ sectionEnabledCount }} / {{ section.toggles.length }}
                      </span>
                    }

                    @if (sectionEnabledCount === 0) {
                      <div class="flex items-center gap-1 mr-2 min-w-fit">
                        <app-icon
                          src="/icons/warning.svg"
                          alt="Warning"
                          color="var(--shikenso-warning-base)"
                          [size]="16"
                        />
                        <span class="text-xs text-shikenso-warning-base">
                          {{
                            'pages.settings.userManagement.addCustomRoleDialog.noSectionPermissions'
                              | translate: { sectionTitle: (section.titleKey | translate) }
                          }}
                        </span>
                      </div>
                    }
                  </div>
                </mat-panel-title>
              </mat-expansion-panel-header>

              <div class="flex flex-wrap gap-x-8 gap-y-4 py-4 px-4">
                @for (toggle of section.toggles; track toggle.key) {
                  @let permissionRestricted = !isViewMode && !canManageToggle(toggle);

                  <div
                    class="flex items-center gap-2 min-w-[240px]"
                    matTooltipPosition="before"
                    [matTooltip]="permissionRestricted ? cannotManagePermissionMessage : ''"
                  >
                    <mat-slide-toggle
                      [checked]="isToggleEnabled(section.key, toggle.key)"
                      [hideIcon]="true"
                      [disabled]="isToggleDisabled(toggle)"
                      (change)="onToggleChange(section.key, toggle.key, $event.checked)"
                    />
                    <mat-label class="text-sm">
                      {{ toggle.labelKey | translate }}
                    </mat-label>
                  </div>
                }
              </div>
            </mat-expansion-panel>
          }
        </div>
      </div>

      <div
        class="sticky bottom-[-1px] left-0 right-0 -mb-8 pt-4 pb-0.5 z-10 bg-shikenso-base-white flex flex-col gap-4"
      >
        <hr class="border-shikenso-purple-base-12" />

        <div class="flex justify-end gap-3">
          @if (isViewMode) {
            <button mat-flat-button color="primary" type="button" (click)="onCloseClick()">
              {{ 'shared.close' | translate }}
            </button>
          } @else {
            <button mat-button type="button" [disabled]="submitLoading()" (click)="onCloseClick()">
              {{ 'shared.cancel' | translate }}
            </button>

            <button mat-flat-button color="primary" type="submit" [disabled]="!form.valid || submitLoading()">
              <p [class.hidden]="submitLoading()">{{ 'shared.save' | translate }}</p>

              @if (submitLoading()) {
                <app-dots-spinner />
              }
            </button>
          }
        </div>
      </div>
    </form>
  </div>
</app-side-dialog>
